import { useMediaQuery } from '@uidotdev/usehooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GlobalSearchBar, GlobalSearchBarHandle } from './GlobalSearchBar';
import { GlobalSearchDropdown } from './GlobalSearchDropdown';
import { GLOBAL_SEARCH_NARROW_QUERY } from 'types/globalSearch';
import { GlobalSearchSheet } from './GlobalSearchSheet';
import { useSharedSearchQuery } from './searchInput';
import { useGlobalSearchOptions } from './useGlobalSearchOptions';
import { useGlobalSearchSelection } from './useGlobalSearchSelection';

/** One bar, one panel, one id — the chrome only ever draws this once. */
const LISTBOX_ID = 'global-search-listbox';

export interface HeaderGlobalSearchProps {
    className?: string;
    /** Fires on every open and close, so the layout can dim the page behind the bar. */
    onOpenChange?: (open: boolean) => void;
}

/**
 * The header's search box with its list attached — as a panel under the box, or, below the
 * breakpoint, as the sheet the box raises.
 *
 * The query, the list and the selection live here and not in the panel, because the field is what
 * names the selected option in `aria-activedescendant` — see `useGlobalSearchSelection`.
 */
export function HeaderGlobalSearch({ className, onOpenChange }: HeaderGlobalSearchProps) {
    const barRef = useRef<GlobalSearchBarHandle>(null);
    const narrow = useMediaQuery(GLOBAL_SEARCH_NARROW_QUERY);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [barOpen, setBarOpen] = useState(false);
    const openSheet = useCallback(() => setSheetOpen(true), []);

    const [query, setQuery, resetQuery] = useSharedSearchQuery(sheetOpen);
    const selection = useGlobalSearchSelection(query);

    // The sheet exists only below the breakpoint, so crossing back up unmounts it without it ever
    // reporting a close — that is the user's own doing and a resize is not. The flag it is raised by
    // has to come down with it, or returning to this width would put the sheet back over the page
    // and take the focus, with nobody having asked for it. The row the pointer left in the sheet
    // goes with it too: this close reports to nobody, so it is the one path that has to forget the
    // selection itself, or the wide bar would open on a row picked on the other surface. Adjusted
    // during render rather than in an effect, so no width is ever painted with the surface the
    // other one had open. It is state and not a ref because the comparison below happens in the
    // render that has to act on it, and a ref written during render is the thing that cannot be
    // read back reliably.
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers
    const [mirroredNarrow, setMirroredNarrow] = useState(narrow);
    if (mirroredNarrow !== narrow) {
        setMirroredNarrow(narrow);
        setSheetOpen(false);
        selection.forget();
    }

    const forgetSelection = (open: boolean) => {
        if (!open) {
            selection.forget();
        }
    };

    const closeSheet = () => {
        setSheetOpen(false);
        forgetSelection(false);
    };
    // Closing the box reports itself through `onOpenChange`, which is what forgets the selection.
    // A row picked from it is a search finished, so the box lets go of the query as well.
    const closeBar = () => {
        resetQuery();
        barRef.current?.close();
    };

    const model = useGlobalSearchOptions({
        query,
        listboxId: LISTBOX_ID,
        surface: narrow ? 'sheet' : 'dropdown',
        open: narrow ? sheetOpen : barOpen,
        close: narrow ? closeSheet : closeBar,
    });
    const keys = selection.keysFor(model.options, model.firstId);

    // The panel under the box is the other half of that: going narrow, the field it hangs off goes
    // away without ever blurring, so this is what closes it and lets the layout take its backdrop
    // down. It stays an effect because closing the panel is the bar's own state to change.
    useEffect(() => {
        if (narrow) barRef.current?.close();
    }, [narrow]);

    const panel = (
        <GlobalSearchDropdown
            model={model}
            listboxId={LISTBOX_ID}
            activeOptionId={keys.activeOptionId}
            variant={narrow ? 'sheet' : 'dropdown'}
            onDidYouMean={setQuery}
        />
    );

    return (
        <>
            <GlobalSearchBar
                ref={barRef}
                className={className}
                query={query}
                onQueryChange={setQuery}
                // At this width the box is only the way in, so it is a button that raises the
                // sheet — the sheet carries the field. A box that opened on being focused would
                // leave the keyboard no way past it, since every way back out lands one Tab away.
                onActivate={narrow ? openSheet : undefined}
                onOpenChange={(open) => {
                    setBarOpen(open);
                    forgetSelection(open);
                    onOpenChange?.(open);
                }}
                // The box stays on screen after its panel goes, so a dismissed search would wait in
                // it. The sheet and the dialog need no such call: each puts the query back on opening.
                onDismiss={resetQuery}
                panelId={LISTBOX_ID}
                {...keys}
                panel={panel}
            />
            {narrow && (
                <GlobalSearchSheet
                    open={sheetOpen}
                    onOpenChange={(open) => {
                        setSheetOpen(open);
                        forgetSelection(open);
                    }}
                    listboxId={LISTBOX_ID}
                    query={query}
                    onQueryChange={setQuery}
                    {...keys}
                    panel={panel}
                />
            )}
        </>
    );
}
